import axios, { AxiosResponse } from 'axios'
import Cookies from 'js-cookie'
import moment, { Moment } from 'moment'

import config from '../config'

class ClientSDK {
  private static _instance: ClientSDK | null = null
  private readonly baseUrl: string | null
  private readonly establishmentId: number | null
  private authenticationDeferred: Promise<string> | null = null

  constructor(baseUrl: string, establishmentId: number) {
    this.baseUrl = baseUrl
    this.establishmentId = establishmentId
  }

  public authenticate(): Promise<string> {
    if (null !== this.authenticationDeferred) {
      return this.authenticationDeferred
    }

    if (!this.expiredAt.isValid()) {
      this.clearTokens()
    }

    if (!this.accessToken || this.isExpired()) {
      return this.getNewTokens()
    }

    return new Promise<string>((resolve, reject) => {
      if (this.accessToken) {
        resolve(this.accessToken)
      } else {
        reject()
      }
    })
  }

  public getNewTokens(): Promise<string> {
    this.authenticationDeferred = new Promise<string>((resolve, reject) => {
      axios({
        method: 'get',
        url: this.baseUrl + '/auth-api',
      })
        .then(
          response => {
            this.authenticationDeferred = null
            if (!response.data.access_token || !response.data.refresh_token) {
              reject()
            }

            this.setTokens(response.data)

            resolve(response.data.access_token)
          },
          () => {
            this.authenticationDeferred = null
            window.location.href = '/logout'
            reject()
          },
        )
        .catch(() => {
          this.authenticationDeferred = null
          window.location.href = '/logout'
          reject()
        })
    })

    return this.authenticationDeferred
  }

  public refresh(): Promise<string> {
    this.authenticationDeferred = new Promise<string>((resolve, reject) => {
      axios({
        method: 'post',
        url: this.baseUrl + '/api/refresh-token',
        headers: {
          'X-Custom-Auth': this.accessToken || '',
        },
        data: {
          refresh_token: this.refreshToken || '',
        },
      }).then(
        response => {
          this.authenticationDeferred = null
          if (!response.data.access_token || !response.data.refresh_token) {
            reject()
          }

          this.setTokens(response.data)

          resolve(response.data.access_token)
        },
        () => {
          this.authenticationDeferred = null
          reject()
        },
      )
    })

    return this.authenticationDeferred
  }

  public setTokens(data: {
    access_token: string
    refresh_token: string
    expires_in: number
  }): void {
    Cookies.set(
      'access_token.' + (this.establishmentId ?? ''),
      data.access_token,
      {
        expires: 365,
      },
    )
    Cookies.set(
      'refresh_token.' + (this.establishmentId ?? ''),
      data.refresh_token,
      {
        expires: 365,
      },
    )
    Cookies.set(
      'token_expired_at.' + (this.establishmentId ?? ''),
      moment().add(data.expires_in, 'seconds').format('YYYY-MM-DD HH:mm:ss'),
      { expires: 365 },
    )
  }

  public clearTokens(): void {
    Cookies.remove('access_token.' + (this.establishmentId ?? ''))
    Cookies.remove('refresh_token.' + (this.establishmentId ?? ''))
    Cookies.remove('token_expired_at.' + (this.establishmentId ?? ''))
  }

  public isExpired(): boolean {
    if (!moment.isMoment(this.expiredAt)) {
      return true
    }

    return this.expiredAt.isBefore(moment().add(10, 'minutes'))
  }

  get accessToken(): string | undefined {
    return Cookies.get('access_token.' + (this.establishmentId ?? ''))
  }

  get refreshToken(): string | undefined {
    return Cookies.get('refresh_token.' + (this.establishmentId ?? ''))
  }

  get expiredAt(): Moment {
    return moment(
      Cookies.get('token_expired_at.' + (this.establishmentId ?? '') || ''),
      'YYYY-MM-DD HH:mm:ss',
    )
  }

  static instance(): ClientSDK {
    if (null === ClientSDK._instance) {
      ClientSDK._instance = new ClientSDK(
        config.client_url,
        typeof CONFIGURATION === 'undefined'
          ? 0
          : (CONFIGURATION as any).establishment,
      )
    }

    return ClientSDK._instance
  }

  get<T>(path: string, params?: any): Promise<AxiosResponse<T>> {
    return this.authenticate().then(accessToken => {
      return axios.get(this.baseUrl + '/api/' + path, {
        headers: {
          'X-Custom-Auth': accessToken,
        },
        params,
      })
    })
  }

  getAnonymous<T>(path: string, params?: any): Promise<AxiosResponse<T>> {
      return axios.get(this.baseUrl + '/api/' + path, {params})
  }

  delete<T>(path: string): Promise<AxiosResponse<T>> {
    return this.authenticate().then(accessToken => {
      return axios.delete(this.baseUrl + '/api/' + path, {
        headers: {
          'X-Custom-Auth': accessToken,
        },
      })
    })
  }

  post<T>(path: string, data: any): Promise<AxiosResponse<T>> {
    return this.authenticate().then(accessToken => {
      return axios.post(this.baseUrl + '/api/' + path, data, {
        headers: {
          'X-Custom-Auth': accessToken,
        },
      })
    })
  }

  patch<T>(path: string, data: any): Promise<AxiosResponse<T>> {
    return this.authenticate().then(accessToken => {
      return axios.patch(this.baseUrl + '/api/' + path, data, {
        headers: {
          'X-Custom-Auth': accessToken,
        },
      })
    })
  }

  put<T>(path: string, data: any): Promise<AxiosResponse<T>> {
    return this.authenticate().then(accessToken => {
      return axios.put(this.baseUrl + '/api/' + path, data, {
        headers: {
          'X-Custom-Auth': accessToken,
        },
      })
    })
  }
}

export default ClientSDK
